import {Component, ComponentRef} from '@angular/core';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {map, of, switchMap, tap, zip} from 'rxjs';
import {QuestionService} from '../../services/api/impl/rest/question.service';
import {ShortcutService} from '../../services/shortcut.service';
import {NativeServiceApiProvider} from "../../../services/native-api-provider.service";
import {ConfirmDialogService} from 'src/app/utils/services/confirm-dialog.service';
import {TranslateService} from '@ngx-translate/core';
import {OffcanvasMenuService} from 'src/app/services/offcanvas-menu.service';
import {SessionInfoService} from 'src/app/services/session-info.service';
import {HostBaseComponent} from "../../model/hostBase/host-base.component";
import {DictComponent} from "../../components/exercises/dict/dict.component";
import { FontSizeService } from 'src/app/services/font-size.service';
import {AuthorizationServiceProvider} from "../../../auth_profile/services/authorization-service.provider";
import {AuthorizedUserDetailsService} from "../../../auth_profile/services/api/authorization.api";
import {AnswerHolderBase, V3ExerciseSession, V3ExerciseSessionQuestion} from "../../model/v3/cspa";

@Component({
    selector: 'app-exercise-host',
    templateUrl: './exercise-host.component.html',
    styleUrls: ['../../model/hostBase/host-base.component.scss'],
    standalone: false
})
export class ExerciseHostComponent extends HostBaseComponent<V3ExerciseSession> {
  private personId?: number;
  private sessionQuestions: V3ExerciseSessionQuestion[] = [];

  constructor(
    route: ActivatedRoute,
    confirmDialogService: ConfirmDialogService,
    translateService: TranslateService,
    offcanvasMenuService: OffcanvasMenuService,
    shortcut: ShortcutService,
    questionService: QuestionService,
    progressInfoService: SessionInfoService,
    private provider: NativeServiceApiProvider,
    private auth: AuthorizationServiceProvider,
    private router: Router,
    fontSizeService: FontSizeService
  ) {
    super(
      route,
      confirmDialogService,
      translateService,
      offcanvasMenuService,
      shortcut,
      questionService,
      progressInfoService,
      fontSizeService
    )
  }

  override ngOnInit(): void {
    super.ngOnInit()
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy()
  }

  retrieveFromParamMap(_: ParamMap) {
  }

  getQuestions() {
    return this.sessionQuestions
  }

  getSession() {
    return zip(this.provider.cspaV3(), this.auth.getAuthDetailsService()).pipe(
      switchMap(([api, authDetailsApi]) =>
        this.getPersonId(authDetailsApi).pipe(
          switchMap(personId =>
            zip(
              api.getSession(personId, this.uuId),
              api.getSessionQuestions(personId, this.uuId),
            )
          ),
        ),
      ),
      tap(([session, questions]) => {
        this.session = session
        this.sessionQuestions = questions
        this.question = this.getQuestion()
        this.question && this.setupQuestionTimings(this.question)
        this.goToQuestion(this.questionNb)
      }),
      map(([session, _]) => session),
    )
  }

  getExerciseName() {
    return this.session?.exerciseName
  }

  getInstruction(){
    return this.question?.questionDef?.instructions
  }

  goToQuestion(index: number | string) {
    if (index === 'finish' || +index > this.getQuestions().length) {
      this.isCheckBtnBlocked = true
      this.router.navigate(
        [`../finish`],
        {
          relativeTo: this.route,
          queryParamsHandling: 'merge'
        }
      ).then()
    } else {
      this.onLoadQuestion(this.getQuestions()[+index - 1])
      this.router.navigate(
        [`../${index}`],
        {
          relativeTo: this.route,
          queryParamsHandling: 'merge'
        }
      ).then()
    }
  }

  finishSession() {
    return this.provider.cspaV3().pipe(
      switchMap(api =>
        this.resolvePersonId().pipe(
          switchMap(personId =>
            api.updateSessionStatus(personId, this.uuId, 'FINISHED'),
          ),
        ),
      ),
    );
  }

  goBack() {
    this.finishSession().subscribe(_ => {
      let redirectPath = this.questionNb !== 'finish' ?
        '../finish'
        : '../../../../../'
      this.router.navigate(
        [`${redirectPath}`],
        {
          relativeTo: this.route,
          queryParamsHandling: 'merge'
        }
      ).then()
    });
  }


  postAnswerAndGoToQuestion() {
    this.provider.cspaV3().pipe(
      switchMap(api =>
        this.resolvePersonId().pipe(
          switchMap(personId => {
            const answerHolder = this.toAnswerHolder(this.question)
            if (!answerHolder) {
              return of(null)
            }
            return api.submitQuestionAnswer(
              personId,
              this.uuId,
              +this.questionNb - 1,
              answerHolder,
            )
          }),
        ),
      )
    ).subscribe(_ => {
      this.goToQuestion(+this.questionNb + 1)
    })
  }

  prepareAudioProgressEmitter(componentRef: ComponentRef<DictComponent>) {
    componentRef.instance.audioProgressEmitter.subscribe((progress: number) => {
        if (progress == -1) {}
        else
          this.progressInfoService.setSessionInfo({
            questionNb: this.questionNb,
            session: this.session,
            questionType: this.getQuestionType(),
            questionCount: this.getQuestions()?.length,
            progressPercent: progress
          })
      }
    )
  }

  private getPersonId(authDetailsApi: AuthorizedUserDetailsService) {
    if (this.personId != null) {
      return of(this.personId);
    }
    return authDetailsApi.getSelfPersonId().pipe(
      tap(personId => this.personId = personId),
    );
  }

  private resolvePersonId() {
    if (this.personId != null) {
      return of(this.personId);
    }
    return this.auth.getAuthDetailsService().pipe(
      switchMap(authDetailsApi => this.getPersonId(authDetailsApi)),
    );
  }

  private toAnswerHolder(question: any): AnswerHolderBase | null {
    const answer = question?.questionDef?.answerDef?.answerHolder ?? question?.answer;
    if (answer == null) {
      return null;
    }
    const questionType = question?.questionDef?.['@type'] ?? question?.question?.definition?.['@type'];
    switch (questionType) {
      case 'V3Correction':
        return {
          '@type': 'V3CorrectionAnswerHolder',
          response: answer.response ?? answer.correct ?? '',
        } as AnswerHolderBase;
      case 'V3Dictation':
        return {
          '@type': 'V3DictationAnswerHolder',
          answer: answer.answer ?? '',
          passScore: answer.passScore ?? 0,
        } as AnswerHolderBase;
      case 'V3FillMulti':
      case 'V3Jumble':
      case 'V3Audio':
        return {
          '@type': questionType === 'V3FillMulti'
            ? 'V3FillMultiAnswerHolder'
            : questionType === 'V3Jumble'
              ? 'V3JumbleAnswerHolder'
              : 'V3AudioAnswerHolder',
          answer: answer.answer ?? [],
        } as AnswerHolderBase;
      case 'V3Images':
        return {
          '@type': 'V3ImagesAnswerHolder',
          answers: answer.answers ?? [],
        } as AnswerHolderBase;
      case 'V3Qa':
        return {
          '@type': 'V3QaAnswerHolder',
        } as AnswerHolderBase;
      default:
        throw new Error(`Unsupported answer type: ${questionType}`);
    }
  }
}
